/** Persistent XO continuity traversal rail — canonical Signal → Horizon sequence. */
import { PRIMER_CONTINUITY_SEQUENCE } from "./primerThreadNav";
import type { CanonicalTraverseSlug } from "./continuityTraversalCanon";
import { crossPostPhaseFromTraverseSlug } from "./continuityTraversalCanon";

export type ContinuityTraversalStepId =
  | "signal"
  | "preserve"
  | "represent"
  | "witness"
  | "inspect"
  | "replay"
  | "call"
  | "symbol"
  | "horizon";

export type ContinuityTraversalStep = {
  id: ContinuityTraversalStepId;
  index: number;
  shortLabel: string;
  layerTitle: string;
  href: string;
  external?: boolean;
  hint: string;
  primerThreadId: string;
};

export const CONTINUITY_TRAVERSAL_STEPS: readonly ContinuityTraversalStep[] = [
  {
    id: "signal",
    index: 1,
    shortLabel: "Signal",
    layerTitle: "Signal appears",
    href: "/cross-post",
    hint: "A message or action appears — human or AI-assisted",
    primerThreadId: "cross-post",
  },
  {
    id: "preserve",
    index: 2,
    shortLabel: "Preserve",
    layerTitle: "Preserve as record",
    href: "/cross-post?traverse=preserve",
    hint: "The signal becomes a canonical continuity record",
    primerThreadId: "cross-post",
  },
  {
    id: "represent",
    index: 3,
    shortLabel: "Represent",
    layerTitle: "Platform representations",
    href: "/cross-post?traverse=represent",
    hint: "X, LinkedIn, Discord, sites — representations only",
    primerThreadId: "cross-post",
  },
  {
    id: "witness",
    index: 4,
    shortLabel: "Witness",
    layerTitle: "Witness continuity",
    href: "/records/message-bottle-v2?continuity=1",
    hint: "Authorship, acknowledgement, and context linked",
    primerThreadId: "message-bottle-witness",
  },
  {
    id: "inspect",
    index: 5,
    shortLabel: "Inspect",
    layerTitle: "Inspect continuity",
    href: "https://verify.xo-vault.com/vault/verify.html?traverse=1&source=cross-post",
    external: true,
    hint: "Anyone can inspect whether meaning still matches the record",
    primerThreadId: "vault-verification",
  },
  {
    id: "replay",
    index: 6,
    shortLabel: "Replay",
    layerTitle: "Replay continuity",
    href: "/continuity-replay?source=cross-post",
    hint: "A preserved signal returning after time or platform drift",
    primerThreadId: "continuity-replay",
  },
  {
    id: "call",
    index: 7,
    shortLabel: "Call",
    layerTitle: "Live coordination",
    href: "/call?demo=1",
    hint: "Coordination continues with preserved context",
    primerThreadId: "call-demo",
  },
  {
    id: "symbol",
    index: 8,
    shortLabel: "Symbol",
    layerTitle: "Message Bottle",
    href: "/message-bottle",
    hint: "Symbolic vessel — emotional continuity artifact",
    primerThreadId: "message-bottle",
  },
  {
    id: "horizon",
    index: 9,
    shortLabel: "Horizon",
    layerTitle: "Horizon",
    href: "/horizon",
    hint: "What becomes possible when continuity is inspectable",
    primerThreadId: "horizon",
  },
] as const;

import { isXOCenterHost } from "./xo_center_host";
import { traversalStepVisibleOnCenter } from "./xo_center_visibility";

export function traversalStepsForSurface(
  hostname: string,
  pathname: string,
): readonly ContinuityTraversalStep[] {
  if (isXOCenterHost(hostname)) {
    return CONTINUITY_TRAVERSAL_STEPS.filter((s) => traversalStepVisibleOnCenter(s.id));
  }
  const p = pathname.replace(/\/$/, "") || "/";
  if (p === "/xo-center" || p === "/") {
    return CONTINUITY_TRAVERSAL_STEPS.filter((s) => traversalStepVisibleOnCenter(s.id));
  }
  return CONTINUITY_TRAVERSAL_STEPS;
}


const PRIMER_ID_TO_STEP = new Map(
  CONTINUITY_TRAVERSAL_STEPS.map((s) => [s.primerThreadId, s] as const),
);

const STEP_BY_ID = new Map(CONTINUITY_TRAVERSAL_STEPS.map((s) => [s.id, s] as const));

function resolveCrossPostStep(search: string): ContinuityTraversalStep | null {
  const params = new URLSearchParams(search.startsWith("?") ? search : search ? `?${search}` : "");
  const slug = (params.get("traverse") || "signal").trim().toLowerCase() as CanonicalTraverseSlug;
  const phase = crossPostPhaseFromTraverseSlug(slug);
  if (phase === "compose") return STEP_BY_ID.get("signal") ?? null;
  if (phase === "confirm" || phase === "preserve") return STEP_BY_ID.get("preserve") ?? null;
  if (phase === "crosspost") return STEP_BY_ID.get("represent") ?? null;
  if (phase === "verify") return STEP_BY_ID.get("inspect") ?? null;
  return STEP_BY_ID.get("signal") ?? null;
}

export function isContinuityTraversalPath(pathname: string): boolean {
  const p = pathname.replace(/\/$/, "") || "/";
  if (p === "/primer" || p.startsWith("/primer/")) return true;
  if (p === "/cross-post" || p.startsWith("/cross-post")) return true;
  if (p === "/message-bottle" || p.startsWith("/message-bottle/")) return true;
  if (p === "/records/message-bottle-v2" || p.startsWith("/records/")) return true;
  if (p === "/continuity-replay" || p.startsWith("/continuity-replay")) return true;
  if (p === "/horizon" || p.startsWith("/horizon")) return true;
  if (p === "/call" || p.startsWith("/call")) return true;
  if (p === "/call-continuity-demo") return true;
  if (p === "/ledger" || p.startsWith("/ledger")) return true;
  return false;
}

export function resolveTraversalStepFromPath(pathname: string, search = ""): ContinuityTraversalStep | null {
  const p = pathname.replace(/\/$/, "") || "/";
  if (p === "/cross-post" || p.startsWith("/cross-post")) return resolveCrossPostStep(search);
  if (p === "/records/message-bottle-v2" || p.startsWith("/records/")) return STEP_BY_ID.get("witness") ?? null;
  if (p === "/continuity-replay" || p.startsWith("/continuity-replay")) {
    const src = new URLSearchParams(search).get("source") || "";
    if (src === "message-bottle") return STEP_BY_ID.get("replay") ?? null;
    return STEP_BY_ID.get("replay") ?? null;
  }
  if (p === "/message-bottle" || p === "/message-bottle/record") return STEP_BY_ID.get("symbol") ?? null;
  if (p === "/horizon" || p.startsWith("/horizon")) return STEP_BY_ID.get("horizon") ?? null;
  if (p === "/primer") return null;
  if (p === "/primer/why-systems-fail") return STEP_BY_ID.get("signal") ?? null;
  if (p === "/primer/continuity-surfaces") return STEP_BY_ID.get("represent") ?? null;
  if (p === "/primer/trust-continuum") return STEP_BY_ID.get("witness") ?? null;
  if (p === "/primer/defense-layer") return STEP_BY_ID.get("horizon") ?? null;
  if (p.startsWith("/primer/")) {
    const slug = p.slice("/primer/".length);
    const match = PRIMER_CONTINUITY_SEQUENCE.find((t) => t.href === p || t.id === slug);
    if (match) return PRIMER_ID_TO_STEP.get(match.id) ?? null;
  }
  if (p === "/call" || p.startsWith("/call/") || p === "/call-continuity-demo") {
    const params = new URLSearchParams(search.startsWith("?") ? search : search ? `?${search}` : "");
    if (params.get("demo") === "1") return STEP_BY_ID.get("call") ?? null;
    return null;
  }
  return null;
}

export function getAdjacentTraversalSteps(step: ContinuityTraversalStep | null): {
  previous: ContinuityTraversalStep | null;
  next: ContinuityTraversalStep | null;
} {
  return getAdjacentTraversalStepsInList(step, CONTINUITY_TRAVERSAL_STEPS);
}

export function getAdjacentTraversalStepsInList(
  step: ContinuityTraversalStep | null,
  steps: readonly ContinuityTraversalStep[],
): {
  previous: ContinuityTraversalStep | null;
  next: ContinuityTraversalStep | null;
} {
  if (!step) return { previous: null, next: steps[0] ?? null };
  const i = steps.findIndex((s) => s.id === step.id);
  return {
    previous: i > 0 ? steps[i - 1] ?? null : null,
    next: i >= 0 && i < steps.length - 1 ? steps[i + 1] ?? null : null,
  };
}

/** Normalized handoff labels for the public proof visit. */
export const VISITOR_TRAVERSAL_CTA = {
  witness: "Continue to Witness",
  inspect: "Continue to Inspect",
  replay: "Continue to Replay",
  call: "Continue to Call",
  enterCall: "Enter Continuity Call",
} as const;

export function primerPhaseToRailLabel(phase: string): string {
  const map: Record<string, string> = {
    Signal: "Signal",
    Preserve: "Preserve",
    Represent: "Represent",
    Witness: "Witness",
    Inspection: "Inspect",
    Replay: "Replay",
    Symbol: "Symbol",
    "Future implications": "Horizon",
    /* legacy primer phases */
    Fracture: "Signal",
    "Emotional continuity": "Symbol",
    "Inspectable surfaces": "Represent",
    "Live coordination": "Replay",
    Verification: "Inspect",
    Infrastructure: "Witness",
    "Strategic framing": "Horizon",
  };
  return map[phase] ?? phase;
}
